import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { countries } from 'countries-list';
import { getCitiesWithFallback } from '../../services/geonames.service';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { countryTranslations } from '../../components/LocationSelect';
import { formatEventDayMonth } from './eventDate';
import config from '../../config';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Компактный календарь выбора даты
function Calendar({ value, onChange }) {
  const base = value ? new Date(`${value}T00:00:00`) : new Date();
  const [viewMonth, setViewMonth] = useState(new Date(base.getFullYear(), base.getMonth(), 1));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const firstWeekday = (new Date(viewMonth).getDay() + 6) % 7; // Пн = 0

  const cells = [...Array(firstWeekday).fill(null), ...Array.from(
    { length: daysInMonth },
    (_, i) => new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i + 1)
  )];

  return (
    <div className="bg-[#022424] border border-[#233636] rounded-[12px] p-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          className="text-white/70 text-lg px-2 active:text-white"
        >
          ‹
        </button>
        <p className="text-white text-sm font-medium font-raleway">
          {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </p>
        <button
          type="button"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          className="text-white/70 text-lg px-2 active:text-white"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1 text-[11px] text-white/50">{w}</span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} />;
          const disabled = day < today;
          const isSelected = value === toISO(day);
          return (
            <button
              key={toISO(day)}
              type="button"
              disabled={disabled}
              onClick={() => onChange(toISO(day))}
              className={`h-8 rounded-[8px] text-[13px] font-medium ${
                disabled
                  ? 'text-white/25 cursor-not-allowed'
                  : isSelected
                    ? 'bg-[#A1F69E] text-[#010D0D] font-bold'
                    : 'text-white active:bg-[#043939]'
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Селектор города: «Все города ▾» + поиск по списку городов страны пользователя
function CitySelector({ value, onChange, countryCode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cities, setCities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCitiesWithFallback(countryCode)
      .then((list) => {
        if (cancelled) return;
        // В справочнике встречаются города-дубликаты (например, два Кирова) —
        // оставляем уникальные названия, иначе подборка при поиске «залипает»
        setCities([...new Set(list.map((c) => c.name))]);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cities.slice(0, 60);
    const starts = [];
    const includes = [];
    for (const city of cities) {
      const l = city.toLowerCase();
      if (l.startsWith(q)) starts.push(city);
      else if (l.includes(q)) includes.push(city);
    }
    return [...starts, ...includes].slice(0, 60);
  }, [cities, query]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-[64px] rounded-[8px] bg-[#022424] pl-4 pr-4 border border-[#233636] text-white outline-none focus:border-[#a1f69e] flex items-center cursor-pointer"
      >
        <span className={`text-[18px] font-raleway truncate ${value ? 'text-white' : 'text-white/50'}`}>
          {value || 'Все города'}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className={`ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="#a1f69e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full bg-[#022424] border border-[#233636] rounded-[8px] overflow-hidden shadow-xl">
          <div className="flex items-center gap-2 border-b border-[#233636] px-3 py-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                fill="#8fb3a4"
              />
            </svg>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск города…"
              className="h-9 w-full bg-transparent text-white text-[15px] font-raleway outline-none placeholder:text-white/40"
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
                setQuery('');
              }}
              className={`w-full text-left px-3 py-2.5 text-[15px] font-raleway transition-colors hover:bg-[#043939] ${
                !value ? 'text-[#a1f69e] font-semibold' : 'text-white/70'
              }`}
            >
              Все города
            </button>
            {isLoading ? (
              <p className="px-3 py-3 text-[14px] text-white/50 font-raleway">Загружаем города…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-3 text-[14px] text-white/50 font-raleway">Город не найден</p>
            ) : (
              filtered.map((city) => (
                <button
                  key={city}
                  type="button"
                  onClick={() => {
                    onChange(city);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full text-left px-3 py-2.5 text-[15px] font-raleway transition-colors hover:bg-[#043939] ${
                    city === value ? 'text-[#a1f69e] font-semibold' : 'text-white'
                  }`}
                >
                  {city}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Радио-кнопка сортировки (круглая)
function SortRadio({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 h-[56px] w-full px-1 active:opacity-80"
    >
      <span
        className={`size-[22px] rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
          active ? 'border-[#A1F69E]' : 'border-white/40'
        }`}
      >
        {active && <span className="size-[10px] rounded-full bg-[#A1F69E]" />}
      </span>
      <span className="text-white text-[17px] font-raleway">{label}</span>
    </button>
  );
}

function EventsFilters() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const axiosPrivate = useAxiosPrivate();
  const userData = useSelector((state) => state.user.userData);
  const { userId } = useSelector((state) => state.auth);
  const [profileCountry, setProfileCountry] = useState(null);

  // Страна пользователя: из профиля в Redux, а если её нет — подгружаем с бэкенда
  useEffect(() => {
    if (userData?.country) {
      setProfileCountry(userData.country);
      return;
    }
    if (!userId) return;

    let cancelled = false;
    axiosPrivate
      .get(`/users/${userId}`)
      .then((res) => {
        if (!cancelled) setProfileCountry(res.data?.country || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, userId, userData?.country]);

  // Города подгружаются для страны пользователя
  const userCountryCode = useMemo(() => {
    const name = profileCountry;
    if (!name) return 'BY';
    const entry = Object.entries(countries).find(
      ([, country]) => (countryTranslations[country.name] || country.name) === name
    );
    return entry?.[0] ?? 'BY';
  }, [profileCountry]);

  const initialCity = searchParams.get('city') || '';

  const [city, setCity] = useState(initialCity);
  const [sort, setSort] = useState(searchParams.get('sort') || 'new');
  const [date, setDate] = useState(searchParams.get('date') || '');

  const handleApply = () => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sort) params.set('sort', sort);
    if (sort === 'date' && date) params.set('date', date);
    const query = params.toString();
    navigate(`${config.ROUTES.EVENTS.LIST}${query ? `?${query}` : ''}`);
  };

  return (
    // Скролл внутри страницы — календарь не перекрывается нижней кнопкой
    <div className="w-full h-full overflow-y-auto px-4 pt-2 pb-[190px]">
      {/* Место проведения */}
      <h2 className="font-raleway font-semibold text-white text-[26px] mt-3">Фильтры</h2>
      <h2 className="font-raleway font-semibold text-white text-[20px] mt-3">Место проведения</h2>
      <div className="mt-3">
        <CitySelector value={city} onChange={setCity} countryCode={userCountryCode} />
      </div>

      {/* Сортировка — радио-кнопки */}
      <h2 className="font-raleway font-semibold text-white text-[20px] mt-8">Сортировка</h2>
      <div className="mt-1 bg-[#022424] border border-[#233636] rounded-[12px] px-3 divide-y divide-[#233636]">
        <SortRadio active={sort === 'new'} label="Сначала новые" onClick={() => setSort('new')} />
        <SortRadio active={sort === 'date'} label="По дате" onClick={() => setSort('date')} />
      </div>

      {/* Календарь при сортировке по дате */}
      {sort === 'date' && (
        <div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-white/70 text-[15px] font-raleway">Дата проведения</p>
            {date && (
              <button
                type="button"
                onClick={() => setDate('')}
                className="text-[#A1F69E] text-[14px] font-semibold font-raleway"
              >
                Сбросить дату
              </button>
            )}
          </div>
          <Calendar value={date} onChange={setDate} />
          {date && (
            <p className="text-white/60 text-[13px] mt-2 font-raleway text-center">
              Показать мероприятия на {formatEventDayMonth(date)}
            </p>
          )}
        </div>
      )}

      {/* Применить */}
      <div className="fixed bottom-[31px] left-4 right-4 z-10">
        <button
          type="button"
          onClick={handleApply}
          className="w-full h-[56px] rounded-[400px] font-raleway font-medium text-[16px] bg-[#A1F69E] text-[#010D0D] active:bg-[#7DD279]"
        >
          Применить
        </button>
      </div>
    </div>
  );
}

export default EventsFilters;
